import { getRange, getTimeInterval, useGetQuery, useRouteParams } from "@quortex/front-common"
import { useSearch } from "@tanstack/react-location"
import { getClosestRoundedTimestamp, getStepFromDuration } from "@/helpers/charts"
import type {
  CdnStatisticsParams,
  CommonCdnParams,
  SegmentStatisticsParams,
  StatisticsParams
} from "@/queries/types"

/**
 * @returns /workspaces/{workspaceUuid}/statistics/
 */
const getOverallStatisticsUrl = ({ orgUuid, workspaceUuid }: StatisticsParams) =>
  `/workspaces/${workspaceUuid}/statistics/?org=${orgUuid}`

const getOverallCdnStatisticsUrl = ({
  orgUuid,
  workspaceUuid,
  step,
  startTime,
  endTime,
  unit
}: CommonCdnParams) =>
  `/workspaces/${workspaceUuid}/cdns/statistics/?org=${orgUuid}${unit ? `&unit=${unit}` : ""}${step ? `&step=${step}` : ""}${startTime ? `&starttime=${startTime}` : ""}${endTime ? `&endtime=${endTime}` : ""}`

const getCdnStatisticsUrl = ({
  orgUuid,
  workspaceUuid,
  step,
  startTime,
  endTime,
  cdnUuid,
  unit
}: CdnStatisticsParams) =>
  `/workspaces/${workspaceUuid}/cdns/${cdnUuid}/statistics/?org=${orgUuid}${unit ? `&unit=${unit}` : ""}${step ? `&step=${step}` : ""}${startTime ? `&starttime=${startTime}` : ""}${endTime ? `&endtime=${endTime}` : ""}`

const getSegmentStatisticsUrl = ({
  orgUuid,
  workspaceUuid,
  step,
  startTime,
  endTime,
  segmentUuid,
  unit
}: SegmentStatisticsParams) =>
  `/workspaces/${workspaceUuid}/segments/${segmentUuid}/statistics/?org=${orgUuid}${unit ? `&unit=${unit}` : ""}${step ? `&step=${step}` : ""}${startTime ? `&starttime=${startTime}` : ""}${endTime ? `&endtime=${endTime}` : ""}`

export const useOverallStatistics = (params?: StatisticsParams) => {
  const mergedParams = useRouteParams(params)
  const url = getOverallStatisticsUrl(mergedParams)

  return useGetQuery<api.switchns.OverallStatistics>("switch", url)
}

export const useOverallCdnStatistics = (params?: CommonCdnParams) => {
  const mergedParams = useRouteParams(params)
  const search = useSearch()
  const range = getRange(search)
  const [rangeStartTime, rangeEndTime] = getTimeInterval(range)

  const duration = rangeEndTime - rangeStartTime
  const startTime = getClosestRoundedTimestamp(rangeStartTime, duration)
  const endTime = getClosestRoundedTimestamp(rangeEndTime, duration)
  const step = getStepFromDuration(duration) / 1000

  const url = getOverallCdnStatisticsUrl({
    ...mergedParams,
    endTime,
    startTime,
    step
  })

  return useGetQuery<api.switchns.OverallCdnStatistics>("switch", url)
}

export const useCdnStatistics = (params?: CdnStatisticsParams) => {
  const mergedParams = useRouteParams(params)
  const search = useSearch()
  const range = getRange(search)
  const [rangeStartTime, rangeEndTime] = getTimeInterval(range)

  const duration = rangeEndTime - rangeStartTime
  const startTime = getClosestRoundedTimestamp(rangeStartTime, duration)
  const endTime = getClosestRoundedTimestamp(rangeEndTime, duration)
  const step = getStepFromDuration(duration) / 1000
  const url = getCdnStatisticsUrl({
    ...mergedParams,
    endTime,
    startTime,
    step
  })

  return useGetQuery<api.switchns.CdnStatistics>("switch", url)
}

export const useSegmentStatistics = (params?: SegmentStatisticsParams) => {
  const mergedParams = useRouteParams(params)
  const search = useSearch()
  const range = getRange(search)
  const [rangeStartTime, rangeEndTime] = getTimeInterval(range)

  const duration = rangeEndTime - rangeStartTime
  const startTime = getClosestRoundedTimestamp(rangeStartTime, duration)
  const endTime = getClosestRoundedTimestamp(rangeEndTime, duration)
  const step = getStepFromDuration(duration) / 1000
  const url = getSegmentStatisticsUrl({
    ...mergedParams,
    endTime,
    startTime,
    step
  })

  return useGetQuery<api.switchns.SegmentStatistics>("switch", url)
}
